import { Amplify, Auth } from "aws-amplify";
import { Login } from "kh-design-system/components";
import React, { useEffect, useState } from "react";
import { useUser } from "../../contexts/AuthContext";
import { useEvent } from "../../contexts/EventContext";
import { authentication, authenticationModuleNames } from "../../utils/constants";
import { setJwtToken } from "../../crud/axios";
import { getSubRoute } from "../../utils/core";
import { logout } from "../../reusable/common";
import { authConfig, defaultAwsConfig, userPoolWebClientId } from "../../config/aws-exports";

const LoginWrapper = ({
	emailValue,
	passwordValue,
	setIsSignIn,
	setIsSignUp,
	setSwitchComponent,
	setRequestOtpEmail,
	isError,
	setIsError,
	toasterMessage,
	setToasterMessage,
	notificationPopup,
	setNotificationPopup,
	...props
}: {
	emailValue: string;
	passwordValue: string;
	setIsSignIn: React.Dispatch<React.SetStateAction<boolean>>;
	setIsSignUp: React.Dispatch<React.SetStateAction<boolean>>;
	setSwitchComponent: React.Dispatch<React.SetStateAction<string>>;
	setRequestOtpEmail: React.Dispatch<React.SetStateAction<string>>;
	isError: boolean;
	setIsError: React.Dispatch<React.SetStateAction<boolean>>;
	toasterMessage: string;
	setToasterMessage: React.Dispatch<React.SetStateAction<string>>;
	notificationPopup: boolean;
	setNotificationPopup: React.Dispatch<React.SetStateAction<boolean>>;
	[key: string]: any;
}) => {
	const { eventDetails, setShowPageLoader } = useEvent();
	const { setUser } = useUser();
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	const primaryColor = eventDetails?.gallery_brand_color;
	const fontFamily = eventDetails?.gallery_font;

	const [loading, setLoading] = useState(false);
	const [loginError, setLoginError] = useState("");

	const handleLoginEmailPassword = async (
		isFormValid: boolean,
		email: string,
		password: string
	) => {
		if (isFormValid) {
			setLoading(true);
			try {
				// Check if there's an existing session and sign out if necessary
				try {
					await Auth.currentAuthenticatedUser();
					await logout();
				} catch (error) {
					// No current user, proceed with sign in
				}

				const userDetails = await Auth.signIn(email, password);

				if (userDetails) {
					const clickedActions = [
						{ clickedKey: "isFaceSearchClicked", openKey: "openFaceSearch" },
						{ clickedKey: "isDownloadClicked", openKey: "openDownloadPopup" }
					];

					clickedActions.forEach(({ clickedKey, openKey }) => {
						if (localStorage.getItem(clickedKey)) {
							localStorage.setItem(openKey, "true");
							localStorage.removeItem(clickedKey);
						}
					});
					const sessionResponse = await Auth.currentSession();
					setJwtToken(sessionResponse?.getIdToken()?.getJwtToken());
					setUser((user: any) => ({
						...user,
						session: sessionResponse,
						user: userDetails
					}));
					setLoading(false);
					setShowPageLoader(false);
				}
			} catch (error) {
				console.error("Error during login:", error);
				if (error instanceof Error) {
					setLoginError(`Login failed: ${error.message}`);
					setIsError(true);
					setToasterMessage(`Login failed: ${error.message}`);
					setNotificationPopup(true);
					setLoading(false);
				} else {
					setLoginError(authentication.error.INVALID_EMAIL_PASSWORD);
					setLoading(false);
				}
			}
		}
	};

	const handleLoginWithOtp = (email: string) => {
		if (email) {
			setIsSignIn(true);
			setRequestOtpEmail(email);
			setSwitchComponent(authenticationModuleNames.OTP_VERIFICATION);
		} else {
			setIsError(true);
			setToasterMessage(authentication.error.REQUIRED_EMAIL);
			setNotificationPopup(true);
		}
	};

	const signInWithGoogle = async () => {
		try {
			await Auth.federatedSignIn({ provider: "Google" });
			localStorage.setItem("path", getSubRoute());
		} catch (error) {
			console.error("Error during Google Sign-In:", error);
		}
	};
	const signInWithAmazon = async () => {
		try {
			await Auth.federatedSignIn({ provider: "Amazon" });
			localStorage.setItem("path", getSubRoute());
		} catch (error) {
			console.error("Error during Amazon Sign-In:", error);
		}
	};

	const signInWithFacebook = async () => {
		try {
			await Auth.federatedSignIn({ provider: "Facebook" });
			localStorage.setItem("path", getSubRoute());
		} catch (error) {
			console.error("Error during Facebook Sign-In:", error);
		}
	};

	useEffect(() => {
		setEmail(emailValue || "");
		setPassword(passwordValue || "");
	}, [emailValue, passwordValue]);

	useEffect(() => {
		Amplify.configure({
			...defaultAwsConfig,
			Auth: { ...defaultAwsConfig.Auth, userPoolWebClientId: userPoolWebClientId }
		});
		Auth.configure({
			...(authConfig as any),
			aws_user_pools_web_client_id: userPoolWebClientId
		});
	}, []);

	return (
		<>
			<Login
				handleLoginEmailPassword={handleLoginEmailPassword}
				handleLoginWithOtp={handleLoginWithOtp}
				loginError={loginError}
				emailValue={email}
				passwordValue={password}
				loading={loading}
				onClickSignUp={() => {
					setIsSignIn(false);
					setIsSignUp(true);
					setSwitchComponent(authenticationModuleNames.SIGN_UP);
				}}
				onClickForgotPassword={() => {
					setSwitchComponent(authenticationModuleNames.FORGOT_PASSWORD);
				}}
				signInWithGoogle={signInWithGoogle}
				signInWithAmazon={signInWithAmazon}
				signInWithFacebook={signInWithFacebook}
				isLogoVisible={false}
				primaryColor={primaryColor}
				fontFamily={fontFamily}
			/>
		</>
	);
};

export default LoginWrapper;
